import { type FormEvent, useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { useMe, useUpdateProfile } from '@/lib/auth';

export function ProfilePage() {
  const me = useMe();
  const update = useUpdateProfile();

  const [name, setName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [savedField, setSavedField] = useState<'name' | 'password' | null>(null);

  useEffect(() => {
    if (me.data?.user.name !== undefined && me.data?.user.name !== null) {
      setName(me.data.user.name);
      setOriginalName(me.data.user.name);
    } else if (me.data) {
      setName('');
      setOriginalName('');
    }
  }, [me.data?.user.name]);

  if (!me.data) return null;

  const user = me.data.user;
  const nameDirty = name !== originalName;
  const passwordsMatch = password.length > 0 && password === confirmation;

  const saveName = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!nameDirty) return;
    try {
      await update.mutateAsync({ name: name.trim() });
      setOriginalName(name.trim());
      setSavedField('name');
      setTimeout(() => setSavedField(null), 2000);
    } catch {
      /* surfaced below */
    }
  };

  const savePassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!passwordsMatch) return;
    try {
      await update.mutateAsync({ password });
      setPassword('');
      setConfirmation('');
      setSavedField('password');
      setTimeout(() => setSavedField(null), 2000);
    } catch {
      /* surfaced below */
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Profile</h1>
        <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">
          Signed in as <span className="font-mono">{user.email}</span>
          {user.is_verified ? (
            <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">
              verified
            </span>
          ) : (
            <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
              unverified
            </span>
          )}
          {user.is_superuser && (
            <span className="ml-2 rounded bg-forge-100 dark:bg-forge-900 px-1.5 py-0.5 text-xs text-forge-700 dark:text-forge-400">
              superuser
            </span>
          )}
        </p>
      </div>

      <form
        onSubmit={saveName}
        className="space-y-3 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5"
      >
        <h2 className="font-medium">Display name</h2>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="(unset)"
        />
        <div className="flex items-center gap-2">
          <Button type="submit" disabled={!nameDirty || update.isPending}>
            {update.isPending && nameDirty ? 'Saving…' : 'Save name'}
          </Button>
          {savedField === 'name' && (
            <span className="text-sm text-green-700">Saved.</span>
          )}
        </div>
      </form>

      <form
        onSubmit={savePassword}
        className="space-y-3 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 p-5"
      >
        <h2 className="font-medium">Change password</h2>
        <PasswordInput
          autoComplete="new-password"
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="New password"
        />
        <PasswordInput
          autoComplete="new-password"
          minLength={8}
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          placeholder="Confirm new password"
        />
        {confirmation.length > 0 && password !== confirmation && (
          <p className="text-sm text-red-600">Passwords don't match.</p>
        )}
        {update.isError && (
          <p className="text-sm text-red-600">{(update.error as Error).message}</p>
        )}
        <div className="flex items-center gap-2">
          <Button type="submit" disabled={!passwordsMatch || update.isPending}>
            {update.isPending && password ? 'Saving…' : 'Update password'}
          </Button>
          {savedField === 'password' && (
            <span className="text-sm text-green-700">Saved.</span>
          )}
        </div>
        <p className="text-xs text-stone-500 dark:text-stone-400">
          Note: this signs you out of any other sessions on next request.
        </p>
      </form>
    </div>
  );
}
